import type { HealthDocumentationFile } from '@meddihub/mzcr-ui';

export interface HealthDocumentationFolderOption {
	id: string;
	name: string;
	filesCount: number;
}

type FileKind = HealthDocumentationFile['kind'];

/** Folder used last time for each kind of MZČR document – preselected next time. */
const LAST_FOLDER_KEY = 'mzcr.healthDocumentation.lastFolder';

/**
 * Names of the default folders. The folders of a patient are named in the language they were
 * created in (and users rename them), so a folder matches the translated name or the original
 * Czech one.
 */
const DEFAULT_FOLDER_NAMES: Record<
	FileKind,
	{ translationKey: string; original: string }
> = {
	drivingLicenseMedicalReport: {
		translationKey: 'component.mzcr.defaultFolder.drivingLicenseMedicalReport',
		original: 'Lékařské posudky',
	},
	temporaryStorageFile: {
		translationKey: 'component.mzcr.defaultFolder.temporaryStorageFile',
		original: 'Ostatní',
	},
	serviceRequest: {
		translationKey: 'component.mzcr.defaultFolder.serviceRequest',
		original: 'Žádanky',
	},
};

export const readLastFolders = (): Record<string, string> => {
	try {
		return JSON.parse(window.localStorage.getItem(LAST_FOLDER_KEY) ?? '{}');
	} catch {
		return {};
	}
};

export const rememberFolder = (kind: string, folderId: string) => {
	try {
		window.localStorage.setItem(
			LAST_FOLDER_KEY,
			JSON.stringify({ ...readLastFolders(), [kind]: folderId }),
		);
	} catch {
		// the preselection is only a convenience
	}
};

/**
 * The folder to preselect: the one used last time for this kind of document, otherwise the one
 * named after the suggested name / the kind. `null` when none of them exists.
 */
export const pickInitialFolder = (args: {
	folders: HealthDocumentationFolderOption[];
	kind: FileKind;
	suggestedFolderName?: string;
	lastFolders: Record<string, string>;
	t: (key: string) => string;
}): { folderId: string; by: 'last' | 'type' } | null => {
	const { folders, kind, suggestedFolderName, lastFolders, t } = args;

	const lastFolder = folders.find(folder => folder.id === lastFolders[kind]);
	if (lastFolder) {
		return { folderId: lastFolder.id, by: 'last' };
	}

	const byType =
		(suggestedFolderName &&
			folders.find(folder => folder.name === suggestedFolderName)) ||
		folders.find(folder =>
			[
				t(DEFAULT_FOLDER_NAMES[kind].translationKey),
				DEFAULT_FOLDER_NAMES[kind].original,
			].includes(folder.name),
		);
	return byType ? { folderId: byType.id, by: 'type' } : null;
};
